// src/api/menuApi.js

const HOST =
  "https://gb3c4b8d5922445-kingsford1.adb.af-johannesburg-1.oraclecloudapps.com/ords/schools";

const ADMIN_LIST = `${HOST}/academic/get/menus/admin/list/`;
const ROLE_LIST = `${HOST}/academic/get/menus/role/list/`;

const UPSERT = `${HOST}/academic/post/menus/admin/upsert/`;
const DEL = `${HOST}/academic/post/menus/admin/delete/`;

/* -------------------- helpers -------------------- */
async function readJsonSafe(res) {
  const text = await res.text().catch(() => "");
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // ORDS can return HTML/text on errors
    return { status: "ERROR", message: text, http_status: res.status };
  }
}

function authHeaders(token) {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// For POST requests without JSON body (query-param style)
function authHeadersNoBody(token) {
  return {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function throwIfError(res, j) {
  if (res.ok && j?.status !== "ERROR") return;

  // ORDS problem+json often uses title/detail
  const msg =
    j?.message ||
    j?.detail ||
    j?.title ||
    j?.error ||
    (typeof j === "string" ? j : null) ||
    `HTTP ${res.status}`;

  throw new Error(msg);
}

function withParams(baseUrl, params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (!s) return;
    qs.set(k, s);
  });
  const q = qs.toString();
  return q ? `${baseUrl}?${q}` : baseUrl;
}

/* -------------------- APIs -------------------- */

/** Admin list: returns {status, menus:[], permissions:[]} */
export async function fetchMenusAdminList({ schoolId, token }) {
  const url = withParams(ADMIN_LIST, { p_school_id: schoolId });

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders(token),
    cache: "no-store",
  });

  const j = await readJsonSafe(res);
  throwIfError(res, j);

  return {
    status: j?.status || "OK",
    menus: Array.isArray(j?.menus) ? j.menus : [],
    permissions: Array.isArray(j?.permissions) ? j.permissions : [],
    school_id: j?.school_id,
  };
}

/** Role list: returns array of items safely */
export async function fetchMenusForRole({ schoolId, roleCode, token }) {
  const url = withParams(ROLE_LIST, {
    p_school_id: schoolId,
    p_role_code: roleCode,
  });

  const res = await fetch(url, {
    method: "GET",
    headers: authHeaders(token),
    cache: "no-store",
  });

  const j = await readJsonSafe(res);
  throwIfError(res, j);

  if (Array.isArray(j?.items)) return j.items;
  if (Array.isArray(j)) return j;
  return [];
}

/**
 * ✅ UPSERT (INDIVIDUAL PARAMS)
 * ORDS handler expects:
 *   :p_school_id, :p_role_code, :p_menu_def_id, :p_is_active
 *
 * payload can contain:
 *   school_id, role_code, menu_def_id OR menu_id, is_active
 */
export async function upsertMenuPermission({ payload, token }) {
  const url = withParams(UPSERT, {
    p_school_id: payload?.school_id,
    p_role_code: payload?.role_code,
    p_menu_def_id: payload?.menu_def_id ?? payload?.menu_id,
    p_is_active: payload?.is_active ?? "Y",
  });

  const res = await fetch(url, {
    method: "POST",
    headers: authHeadersNoBody(token),
    cache: "no-store",
  });

  const j = await readJsonSafe(res);
  throwIfError(res, j);
  return j;
}

/**
 * ✅ DELETE (INDIVIDUAL PARAMS)
 * ORDS handler should expect:
 *   :p_school_id, :p_role_code, :p_menu_def_id
 */
export async function deleteMenuPermission({ payload, token }) {
  const url = withParams(DEL, {
    p_school_id: payload?.school_id,
    p_role_code: payload?.role_code,
    p_menu_def_id: payload?.menu_def_id ?? payload?.menu_id,
  });

  const res = await fetch(url, {
    method: "POST",
    headers: authHeadersNoBody(token),
    cache: "no-store",
  });

  const j = await readJsonSafe(res);
  throwIfError(res, j);
  return j;
}
